import { loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { TlfItemIndexReadDto } from "functions/api/tlfItem/TlfItemIndexReadDto";
import { useContext, useEffect, useEffectEvent, useState } from "react";
import { Spinner } from "react-bootstrap";
import { eHierarchyLevel } from "../../functions/api/enums";
import { IRequestStatus } from "../../functions/api/interface";
import { searchAllTlfs } from "../../functions/api/tlfItem/searchAllTlfs";
import { TlfItemTable } from "../common/tlfItemTable/TlfItemTable";

/**
 * Inner table used by the search results page to render a list of TLF
 * items for a specific category (e.g. Templates or ToC Items).
 *
 * @param props - Destructured props object.
 * @param props.id - DOM id used for the table instance.
 * @param props.label - Label for the table section shown to users.
 * @param props.searchResults - Array of TLF index DTOs to display.
 * @param props.searchStatus - Request status controlling loading and errors.
 */
const InnerTable = ({
  id,
  label,
  searchResults,
  searchStatus,
}: {
  id: string;
  label: string;
  searchResults: TlfItemIndexReadDto[];
  searchStatus: IRequestStatus<string>;
}) => (
  <>
    {searchStatus.requesting ? (
      <div className="my-2">
        Loading search results for {label}...
        <Spinner
          className="mx-1"
          animation="border"
          size="sm"
          variant="dark"
        />
      </div>
    ) : searchStatus.error ? (
      <p className="text-danger">{searchStatus.errorText}</p>
    ) : searchResults.length === 0 ? (
      <p>No results for {label}</p>
    ) : (
      <div style={{ height: "400px" }}>
        <TlfItemTable
          id={id}
          label={label}
          searchResults={searchResults}
          showSearchResults={label === "Templates"}
          showTocSearchResults={label === "ToC Items"}
        />
      </div>
    )}
  </>
);

InnerTable.displayName = "InnerTable";

/**
 * Search results component that queries TLFs using `searchText` and shows
 * results split into Templates and ToC Items.
 *
 * @param props - Destructured props object.
 * @param props.searchText - The text to search for across TLFs.
 */
export const SearchResultsTlf = ({ searchText }: { searchText: string }): React.ReactElement => {
  const appSettings = useContext(AppSettingsContext);
  const [searchStatus, setSearchStatus] = useState<IRequestStatus<string>>({
    requesting: false,
    error: false,
  });
  const [searchResults, setSearchResults] = useState<TlfItemIndexReadDto[]>([]);

  // Run search
  const search = useEffectEvent(async (searchText: string) => {
    const response = await loadWrapper(
      searchAllTlfs,
      searchText,
      { ...searchStatus, error: false, errorText: undefined },
      setSearchStatus,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (response?.success) {
      setSearchResults(response.data);
    } else if (response && response.success === false) {
      setSearchResults([]);
    }
  });

  useEffect(() => {
    search(searchText);
  }, [searchText]);

  return (
    <>
      <InnerTable
        id="searchResultsTemplates"
        label="Templates"
        searchResults={searchResults.filter(
          (r) => r.primaryHierarchyLevel !== eHierarchyLevel.Report,
        )}
        searchStatus={searchStatus}
      />
      <InnerTable
        id="searchResultsTocItems"
        label="ToC Items"
        searchResults={searchResults.filter(
          (r) => r.primaryHierarchyLevel === eHierarchyLevel.Report,
        )}
        searchStatus={searchStatus}
      />
    </>
  );
};

SearchResultsTlf.displayName = "SearchResultsTlf";
